/**
 * Drives a loaded humanoid (Mixamo or VRM) from a sampled pose: head from tracking, two-bone
 * arm IK to the hands with a sensible pole, spine lean, hips under the head, procedural
 * stepping legs with two-bone IK, finger curl, a speaking nod, and an idle layer (breathing,
 * weight shift, head drift) that fades in while the remote user holds still.
 */
import * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';
import type { SampledHand, SampledPose } from './PoseBuffer.ts';
import { aimBone, recordRest, setWorldQuaternion, solveTwoBone, type BoneRest } from './ik.ts';

type Side = 'left' | 'right';

interface Finger {
  phalanges: { bone: THREE.Object3D; restLocal: THREE.Quaternion; axisLocal: THREE.Vector3 }[];
}

interface Limb {
  upper: BoneRest;
  lower: BoneRest;
  end: BoneRest;
  lengthUpper: number;
  lengthLower: number;
}

interface Bones {
  hips: THREE.Object3D;
  spine: BoneRest[];
  neck: BoneRest | undefined;
  head: BoneRest;
  arms: Record<Side, Limb | undefined>;
  legs: Record<Side, Limb | undefined>;
  fingers: Record<Side, Finger[]>;
}

interface FootState {
  planted: THREE.Vector3;
  from: THREE.Vector3;
  to: THREE.Vector3;
  progress: number;
  /** Which way the foot points, kept while it is planted. */
  yaw: number;
  fromYaw: number;
  toYaw: number;
}

function newFoot(): FootState {
  return {
    planted: new THREE.Vector3(),
    from: new THREE.Vector3(),
    to: new THREE.Vector3(),
    progress: 1,
    yaw: 0,
    fromYaw: 0,
    toYaw: 0,
  };
}

/** Shortest signed angle from a to b. */
function angleTo(a: number, b: number): number {
  return Math.atan2(Math.sin(b - a), Math.cos(b - a));
}

const UP = new THREE.Vector3(0, 1, 0);
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _q1 = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _m = new THREE.Matrix4();

const MIXAMO: Record<string, string> = {
  hips: 'Hips',
  spine: 'Spine',
  spine1: 'Spine1',
  spine2: 'Spine2',
  neck: 'Neck',
  head: 'Head',
  headTop: 'HeadTop_End',
  leftArm: 'LeftArm',
  leftForeArm: 'LeftForeArm',
  leftHand: 'LeftHand',
  rightArm: 'RightArm',
  rightForeArm: 'RightForeArm',
  rightHand: 'RightHand',
  leftUpLeg: 'LeftUpLeg',
  leftLeg: 'LeftLeg',
  leftFoot: 'LeftFoot',
  leftToe: 'LeftToeBase',
  rightUpLeg: 'RightUpLeg',
  rightLeg: 'RightLeg',
  rightFoot: 'RightFoot',
  rightToe: 'RightToeBase',
};
const FINGERS = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'];
const VRM_FINGERS: Record<string, string[]> = {
  Thumb: ['ThumbMetacarpal', 'ThumbProximal', 'ThumbDistal'],
  Index: ['IndexProximal', 'IndexIntermediate', 'IndexDistal'],
  Middle: ['MiddleProximal', 'MiddleIntermediate', 'MiddleDistal'],
  Ring: ['RingProximal', 'RingIntermediate', 'RingDistal'],
  Pinky: ['LittleProximal', 'LittleIntermediate', 'LittleDistal'],
};

export class AvatarRig {
  readonly root: THREE.Object3D;
  private readonly bones: Bones;
  /** Character facing direction in rest pose (world), from the feet. */
  private readonly restForward = new THREE.Vector3(0, 0, 1);
  private readonly restEyeHeight: number;
  private readonly hipsRestHeight: number;
  /** Eye point relative to the head bone at rest: metres up and forward. */
  private eyeUp = 0.08;
  private eyeForward = 0.085;
  /** Head bone relative to the hips at rest: metres up and forward. */
  private headUp = 0.6;
  private headForward = 0.02;
  /** Neck bone to head bone at rest, metres. */
  private neckLength = 0.1;
  /** The root's world orientation when the rest pose was recorded. */
  private readonly restRootQuat = new THREE.Quaternion();
  /** How the body has turned since the rest pose; spine, neck and head frames add the twist. */
  private readonly frame = new THREE.Quaternion();
  private readonly twistFrame = new THREE.Quaternion();
  /** Head yaw minus body yaw, shared out over the spine and neck. */
  private headTwist = 0;
  private readonly hipsRestOffset: THREE.Vector3;
  private yaw = 0;
  private yawInitialised = false;
  /** True while the body turns to catch up with the head (hysteresis on the dead zone). */
  private turning = false;
  private readonly feet: Record<Side, FootState> = { left: newFoot(), right: newFoot() };
  /** The foot that stepped last, so steps alternate. */
  private lastStep: Side = 'right';
  /** Smoothed ground velocity of the hips, m/s. */
  private readonly velocity = new THREE.Vector3();
  private readonly lastHips = new THREE.Vector3();
  private hasLastHips = false;
  private feetInitialised = false;
  private time = 0;
  /** 0 while the tracked head moves, rising to 1 after about a second of holding still. */
  private stillness = 0;
  private headMotion = 1;
  private readonly lastHeadPos = new THREE.Vector3();
  private readonly lastHeadQuat = new THREE.Quaternion();
  private hasLastHead = false;
  /** Per-avatar offsets so a room of idle people does not breathe in step. */
  private readonly idlePhase = Math.random() * 100;
  private readonly breathRate = (2 * Math.PI) / (3.8 + Math.random() * 1.2);
  private readonly idleHead = new THREE.Vector3();
  private readonly idleTurn = new THREE.Quaternion();
  private breath = 0;
  private hipWidth = 0.18;
  private readonly footLength: number;
  readonly ok: boolean;

  constructor(root: THREE.Object3D, vrm?: VRM) {
    this.root = root;
    root.updateMatrixWorld(true);
    const find = (key: string): THREE.Object3D | undefined => {
      if (vrm) return vrm.humanoid.getRawBoneNode(vrmName(key) as never) ?? undefined;
      const name = MIXAMO[key];
      let found: THREE.Object3D | undefined;
      root.traverse((o) => {
        if (!found && o.name.replace(/^mixamorig\d*:?/, '') === name) found = o;
      });
      return found;
    };
    const hips = find('hips');
    const head = find('head');
    root.getWorldQuaternion(this.restRootQuat);
    this.ok = !!hips && !!head;
    if (!hips || !head) {
      this.bones = {
        hips: root,
        spine: [],
        neck: undefined,
        head: recordRest(root as THREE.Bone, undefined),
        arms: { left: undefined, right: undefined },
        legs: { left: undefined, right: undefined },
        fingers: { left: [], right: [] },
      };
      this.restEyeHeight = 1.6;
      this.hipsRestHeight = 0.95;
      this.hipsRestOffset = new THREE.Vector3();
      this.footLength = 0.25;
      return;
    }
    // Facing: square to the hip line (left hip minus right hip, crossed with up). The toes are
    // splayed by several degrees on these characters, so a foot is not a good guide; it is
    // the fallback, then +Z.
    const lf = find('leftFoot');
    const lt = find('leftToe');
    const lHip = find('leftUpLeg');
    const rHip = find('rightUpLeg');
    let squared = false;
    if (lHip && rHip) {
      _v1.subVectors(
        lHip.getWorldPosition(new THREE.Vector3()),
        rHip.getWorldPosition(new THREE.Vector3()),
      );
      _v1.y = 0;
      if (_v1.lengthSq() > 1e-6) {
        this.restForward.crossVectors(_v1.normalize(), UP).normalize();
        squared = true;
      }
    }
    if (lf && lt) {
      _v1.subVectors(
        lt.getWorldPosition(new THREE.Vector3()),
        lf.getWorldPosition(new THREE.Vector3()),
      );
      _v1.y = 0;
      if (!squared && _v1.lengthSq() > 1e-6) this.restForward.copy(_v1.normalize());
      this.footLength = Math.max(
        0.15,
        lt
          .getWorldPosition(new THREE.Vector3())
          .distanceTo(lf.getWorldPosition(new THREE.Vector3())),
      );
    } else {
      this.footLength = 0.25;
    }
    const limb = (u: string, l: string, e: string, next: string | undefined): Limb | undefined => {
      const ub = find(u);
      const lb = find(l);
      const eb = find(e);
      if (!ub || !lb || !eb) return undefined;
      const upper = recordRest(ub as THREE.Bone, lb);
      const lower = recordRest(lb as THREE.Bone, eb);
      const end = recordRest(eb as THREE.Bone, next ? find(next) : undefined);
      return { upper, lower, end, lengthUpper: upper.length, lengthLower: lower.length };
    };
    const spineKeys = ['spine', 'spine1', 'spine2'];
    const spineBones = spineKeys.map(find).filter((b): b is THREE.Object3D => !!b);
    const neckBone = find('neck');
    const spine: BoneRest[] = spineBones.map((b, i) =>
      recordRest(b as THREE.Bone, spineBones[i + 1] ?? neckBone ?? head),
    );
    const fingers = (side: Side): Finger[] =>
      FINGERS.map((f) => {
        const names = vrm
          ? VRM_FINGERS[f]!.map((n) => `${side}${n}`)
          : [1, 2, 3].map((i) => `${side === 'left' ? 'Left' : 'Right'}Hand${f}${i}`);
        const phalanges: Finger['phalanges'] = [];
        for (const n of names) {
          let bone: THREE.Object3D | undefined;
          if (vrm) bone = vrm.humanoid.getRawBoneNode(n as never) ?? undefined;
          else
            root.traverse((o) => {
              if (!bone && o.name.replace(/^mixamorig\d*:?/, '') === n) bone = o;
            });
          if (!bone) continue;
          const child = bone.children[0];
          const rest = recordRest(bone as THREE.Bone, child);
          // Curl bends the finger toward the palm (down in T-pose): axis = finger x -up.
          const axisWorld = new THREE.Vector3()
            .crossVectors(rest.restDir, new THREE.Vector3(0, -1, 0))
            .normalize();
          if (f === 'Thumb')
            axisWorld
              .crossVectors(
                rest.restDir,
                side === 'left' ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0, 0, -1),
              )
              .normalize();
          const axisLocal = axisWorld
            .clone()
            .applyQuaternion(rest.restWorldQuat.clone().invert())
            .normalize();
          phalanges.push({ bone, restLocal: bone.quaternion.clone(), axisLocal });
        }
        return { phalanges };
      });
    this.bones = {
      hips,
      spine,
      neck: neckBone ? recordRest(neckBone as THREE.Bone, head) : undefined,
      head: recordRest(head as THREE.Bone, find('headTop')),
      arms: {
        left: limb('leftArm', 'leftForeArm', 'leftHand', undefined),
        right: limb('rightArm', 'rightForeArm', 'rightHand', undefined),
      },
      legs: {
        left: limb('leftUpLeg', 'leftLeg', 'leftFoot', 'leftToe'),
        right: limb('rightUpLeg', 'rightLeg', 'rightFoot', 'rightToe'),
      },
      fingers: { left: fingers('left'), right: fingers('right') },
    };
    const headPos = head.getWorldPosition(new THREE.Vector3());
    const hipsPos = hips.getWorldPosition(new THREE.Vector3());
    // The tracked point is the eyes, which sit well in front of the head bone (the top of the
    // neck), not straight above it. Proportional to the head's length where it is known.
    const headTop = find('headTop');
    const headLength = headTop
      ? headTop.getWorldPosition(new THREE.Vector3()).distanceTo(headPos)
      : 0.19;
    this.eyeUp = 0.42 * headLength;
    this.eyeForward = 0.45 * headLength;
    this.headUp = headPos.y - hipsPos.y;
    this.headForward = _v1.subVectors(headPos, hipsPos).dot(this.restForward);
    if (this.bones.neck) this.neckLength = this.bones.neck.length;
    this.restEyeHeight = headPos.y + this.eyeUp;
    this.hipsRestHeight = hipsPos.y;
    this.hipsRestOffset = new THREE.Vector3().subVectors(
      hipsPos,
      root.getWorldPosition(new THREE.Vector3()),
    );
    const lul = find('leftUpLeg');
    const rul = find('rightUpLeg');
    if (lul && rul)
      this.hipWidth = Math.max(
        0.12,
        lul
          .getWorldPosition(new THREE.Vector3())
          .distanceTo(rul.getWorldPosition(new THREE.Vector3())),
      );
  }

  /** Eye height of the unscaled model. */
  get modelEyeHeight(): number {
    return this.restEyeHeight;
  }

  /** Applies a sampled pose. `dt` in seconds. */
  apply(pose: SampledPose, dt: number): void {
    if (!this.ok) {
      this.root.position.set(pose.headPosition.x, 0, pose.headPosition.z);
      return;
    }
    this.time += dt;
    const b = this.bones;
    this.updateStillness(pose, dt);
    const scale = THREE.MathUtils.clamp((pose.height || 1.65) / this.restEyeHeight, 0.7, 1.4);
    this.root.scale.setScalar(scale);

    // Yaw: follow the head with a dead zone so the body does not twitch with every glance.
    _v1.set(0, 0, -1).applyQuaternion(pose.headQuaternion);
    const headYaw = Math.atan2(-_v1.x, -_v1.z);
    if (!this.yawInitialised) {
      this.yaw = headYaw;
      this.yawInitialised = true;
    } else {
      // The body starts turning when the head is 35 degrees off and keeps turning until it is
      // nearly square again, as people do; moving always lines the body up with the head.
      const delta = angleTo(this.yaw, headYaw);
      if (Math.abs(delta) > THREE.MathUtils.degToRad(35) || pose.moving !== 0) this.turning = true;
      else if (Math.abs(delta) < THREE.MathUtils.degToRad(6)) this.turning = false;
      if (this.turning) this.yaw += delta * Math.min(1, dt * 5);
    }
    // Root orientation: rotate the rest forward onto the body yaw.
    const restYaw = Math.atan2(-this.restForward.x, -this.restForward.z);
    this.root.rotation.set(0, this.yaw - restYaw, 0);
    this.root.updateMatrixWorld(true);
    this.root.getWorldQuaternion(this.frame).multiply(_q1.copy(this.restRootQuat).invert());
    this.headTwist = angleTo(this.yaw, headYaw);

    // Hips under the head, pulled back a little when the head pitches forward.
    _v2.set(0, 0, -1).applyQuaternion(pose.headQuaternion);
    const pitchLean = THREE.MathUtils.clamp(-_v2.y, -0.5, 0.8);
    const bodyForward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const shift = this.idle(pose, bodyForward);
    // The head bone sits behind and below the tracked eyes, turning with the head.
    const headTurn = _q1.copy(pose.headQuaternion).premultiply(this.idleTurn);
    const head = this.idleHead
      .clone()
      .sub(
        new THREE.Vector3(0, this.eyeUp * scale, -this.eyeForward * scale).applyQuaternion(
          headTurn,
        ),
      );
    const bodyRight = new THREE.Vector3().crossVectors(bodyForward, UP).normalize();
    const hipsTarget = head
      .clone()
      .addScaledVector(UP, -this.headUp * scale * (1 - 0.06 * pitchLean))
      .addScaledVector(bodyForward, -this.headForward * scale - 0.1 * pitchLean)
      // Weight shift: the hips drift over one foot and back (the leg IK bends the other knee).
      .addScaledVector(bodyRight, shift * 0.022 * (0.4 + 0.6 * this.stillness))
      .addScaledVector(UP, this.breath * 0.0015 - Math.abs(shift) * 0.004 * this.stillness);
    // Place the root so the hips land on the target.
    this.root.updateMatrixWorld(true);
    const hipsNow = b.hips.getWorldPosition(_v3);
    this.root.position.add(hipsTarget.clone().sub(hipsNow));
    this.root.updateMatrixWorld(true);

    // Spine chain: aim each segment from the hips toward the head, sharing the lean.
    const hipsPos = b.hips.getWorldPosition(new THREE.Vector3());
    const neckTarget = head
      .clone()
      .addScaledVector(UP, -this.neckLength * scale)
      .addScaledVector(bodyForward, -0.01 * scale);
    const n = b.spine.length;
    b.spine.forEach((seg, i) => {
      const from = seg.bone.getWorldPosition(new THREE.Vector3());
      const t = (i + 1) / n;
      const target = hipsPos
        .clone()
        .lerp(neckTarget, t)
        .add(neckTarget.clone().sub(hipsPos).multiplyScalar(0.15));
      // Breathing: the chest rises and comes forward a little on the in-breath.
      if (i >= n - 2) {
        const k = i === n - 1 ? 1 : 0.5;
        target
          .addScaledVector(bodyForward, this.breath * 0.009 * k)
          .addScaledVector(UP, this.breath * 0.004 * k);
      }
      // The chest turns a little toward where the head looks (10, 20, 30 percent up the
      // spine) so a glance is not all taken at the top of the neck.
      aimBone(seg, target.sub(from), this.twisted(0.1 * (i + 1) * (3 / n)));
      seg.bone.updateWorldMatrix(false, true);
    });
    if (b.neck) {
      const from = b.neck.bone.getWorldPosition(new THREE.Vector3());
      aimBone(b.neck, head.clone().sub(from), this.twisted(0.6));
      b.neck.bone.updateWorldMatrix(false, true);
    }

    // Head: match the tracked orientation, plus a nod while speaking.
    _q1.copy(pose.headQuaternion).premultiply(this.idleTurn);
    if (pose.speaking) {
      _q2.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.sin(this.time * 9) * 0.035);
      _q1.multiply(_q2);
    }
    this.orientToTracked(b.head, _q1, false);

    // Ground velocity of the body, for the gait and the arm swing. A jump of more than 0.8 m
    // between frames is a teleport even if the frame that said so was interpolated away.
    const jumped = this.hasLastHips && hipsTarget.distanceTo(this.lastHips) > 0.8;
    const moving = jumped ? 2 : pose.moving;
    if (this.hasLastHips && dt > 0 && moving !== 2) {
      const v = hipsTarget.clone().sub(this.lastHips).setY(0).divideScalar(dt);
      if (v.length() > 3) v.setLength(3);
      this.velocity.lerp(v, Math.min(1, dt * 8));
    } else if (moving === 2) this.velocity.set(0, 0, 0);
    this.lastHips.copy(hipsTarget);
    this.hasLastHips = true;

    // Arms.
    this.applyArm('left', pose.hands[0], bodyForward, scale, hipsTarget);
    this.applyArm('right', pose.hands[1], bodyForward, scale, hipsTarget);

    // Fingers.
    this.applyFingers('left', pose.hands[0]);
    this.applyFingers('right', pose.hands[1]);

    // Legs.
    this.applyLegs(hipsTarget, bodyForward, scale, dt, moving);
  }

  /**
   * How still the sender's head is. Interpolated poses are smooth, so speed alone tells a
   * parked desktop user (or a VR user standing very still) from someone looking around.
   */
  private updateStillness(pose: SampledPose, dt: number): void {
    if (this.hasLastHead && dt > 0) {
      const lin = pose.headPosition.distanceTo(this.lastHeadPos) / dt;
      const ang = this.lastHeadQuat.angleTo(pose.headQuaternion) / dt;
      const motion = lin / 0.03 + ang / 0.12;
      this.headMotion += (motion - this.headMotion) * Math.min(1, dt * 6);
    }
    this.lastHeadPos.copy(pose.headPosition);
    this.lastHeadQuat.copy(pose.headQuaternion);
    this.hasLastHead = true;
    const target = this.headMotion < 1 && pose.moving === 0 ? 1 : 0;
    const rate = target > this.stillness ? 0.9 : 5;
    this.stillness += (target - this.stillness) * Math.min(1, dt * rate);
  }

  /**
   * The idle layer: breathing (always), and while the sender holds still a slow weight shift,
   * a small head drift and tilt. Sums of slow sines at unrelated rates, so it never visibly
   * loops. Writes the head position and turn the body is built from; returns the weight
   * shift (-1..1, toward the body's right).
   */
  private idle(pose: SampledPose, bodyForward: THREE.Vector3): number {
    const t = this.time + this.idlePhase;
    const s = this.stillness;
    this.breath = Math.sin(t * this.breathRate);
    const shift = 0.75 * Math.sin(t * 0.71) + 0.25 * Math.sin(t * 1.83 + 1.3);
    const right = _v1.crossVectors(bodyForward, UP).normalize();
    this.idleHead
      .copy(pose.headPosition)
      .addScaledVector(right, shift * 0.012 * s)
      .addScaledVector(bodyForward, 0.004 * Math.sin(t * 0.53) * s)
      .addScaledVector(UP, this.breath * 0.002 * s);
    const yaw = s * (0.035 * Math.sin(t * 0.29) + 0.015 * Math.sin(t * 0.83 + 0.4));
    const pitch =
      s * (0.018 * Math.sin(t * 0.41 + 2) + 0.008 * Math.sin(t * 1.07) + 0.006 * this.breath);
    const roll = s * (0.012 * Math.sin(t * 0.37 + 1) - 0.012 * shift);
    // World yaw about +Y, then pitch and roll about the body's own axes.
    this.idleTurn.setFromAxisAngle(UP, yaw);
    this.idleTurn.multiply(_q2.setFromAxisAngle(right, pitch));
    this.idleTurn.multiply(_q2.setFromAxisAngle(bodyForward, roll));
    return shift;
  }

  /** The body frame turned by a share of the head's twist (0 = body, 1 = head). */
  private twisted(share: number): THREE.Quaternion {
    return this.twistFrame.setFromAxisAngle(UP, this.headTwist * share).multiply(this.frame);
  }

  /** World head position for voice and name tags. */
  headWorldPosition(target = new THREE.Vector3()): THREE.Vector3 {
    return this.bones.head.bone.getWorldPosition(target);
  }

  private orientToTracked(rest: BoneRest, tracked: THREE.Quaternion, isHand: boolean): void {
    // Map rest axes (forward = restForward, up = +Y) onto the tracked frame (forward = -Z, up = +Y).
    const f = new THREE.Vector3(0, 0, -1).applyQuaternion(tracked);
    const u = new THREE.Vector3(0, 1, 0).applyQuaternion(tracked);
    const r = new THREE.Vector3().crossVectors(u, f).normalize();
    u.crossVectors(f, r).normalize();
    _m.makeBasis(r, u, f);
    const target = new THREE.Quaternion().setFromRotationMatrix(_m);
    const rf = this.restForward.clone();
    const ru = new THREE.Vector3(0, 1, 0);
    const rr = new THREE.Vector3().crossVectors(ru, rf).normalize();
    _m.makeBasis(rr, ru, rf);
    const restBasis = new THREE.Quaternion().setFromRotationMatrix(_m);
    _q2.copy(target).multiply(restBasis.invert()).multiply(rest.restWorldQuat);
    void isHand;
    setWorldQuaternion(rest.bone, _q2);
    rest.bone.updateWorldMatrix(false, true);
  }

  private applyArm(
    side: Side,
    hand: SampledHand | null,
    bodyForwardIn: THREE.Vector3,
    scale: number,
    hips: THREE.Vector3,
  ): void {
    const arm = this.bones.arms[side];
    if (!arm) return;
    const shoulder = arm.upper.bone.getWorldPosition(new THREE.Vector3());
    // The chest has turned 30 percent of the way toward the head.
    const bodyForward = bodyForwardIn.clone().applyAxisAngle(UP, this.headTwist * 0.3);
    const sideDir = new THREE.Vector3().crossVectors(UP, bodyForward).normalize();
    if (side === 'right') sideDir.negate();
    let wrist: THREE.Vector3;
    if (hand) {
      wrist = hand.position.clone();
      if (hand.kind === 'controller') {
        // Grip space sits in the palm; the wrist is a little behind along +Z of the grip.
        wrist.add(new THREE.Vector3(0, 0, 0.06).applyQuaternion(hand.quaternion));
      }
    } else {
      // Relaxed: hang beside the body, elbows soft, swinging against the opposite leg when
      // walking (the left arm goes forward with the right foot).
      const opposite = this.feet[side === 'left' ? 'right' : 'left'].planted;
      const lead = THREE.MathUtils.clamp(opposite.clone().sub(hips).dot(bodyForward), -0.35, 0.35);
      const speed = Math.min(1.5, this.velocity.length());
      const swing = lead * 0.55 * Math.min(1, speed / 0.6);
      const t = this.time + this.idlePhase + (side === 'left' ? 0 : 2.1);
      const drift = this.stillness * (0.012 * Math.sin(t * 0.47) + 0.005 * Math.sin(t * 1.3));
      // Nearly straight: the wrist hangs just in front of the shoulder line and clear of the
      // hip, so the elbow only softens backward a little.
      wrist = shoulder
        .clone()
        .addScaledVector(UP, -(arm.lengthUpper + arm.lengthLower) * scale * 0.975)
        .addScaledVector(bodyForward, 0.035 + swing + drift + this.breath * 0.004)
        .addScaledVector(sideDir, 0.035 + this.stillness * 0.006 * Math.sin(t * 0.61));
    }
    // Elbows bend backward and a little out.
    const pole = new THREE.Vector3()
      .addScaledVector(UP, hand ? -0.7 : -0.2)
      .addScaledVector(bodyForward, hand ? -0.45 : -0.9)
      .addScaledVector(sideDir, 0.35)
      .normalize();
    const elbow = solveTwoBone(
      shoulder,
      wrist,
      pole,
      arm.lengthUpper * scale,
      arm.lengthLower * scale,
    );
    const chest = this.twisted(0.3);
    aimBone(arm.upper, elbow.clone().sub(shoulder), chest);
    arm.upper.bone.updateWorldMatrix(false, true);
    const elbowNow = arm.lower.bone.getWorldPosition(new THREE.Vector3());
    aimBone(arm.lower, wrist.clone().sub(elbowNow), chest);
    arm.lower.bone.updateWorldMatrix(false, true);
    if (hand) {
      // Hand orientation: fingers along the tracked forward, back of the hand along tracked up.
      let fwd: THREE.Vector3;
      let up: THREE.Vector3;
      if (hand.kind === 'hand') {
        fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(hand.quaternion);
        up = new THREE.Vector3(0, 1, 0).applyQuaternion(hand.quaternion);
      } else {
        fwd = new THREE.Vector3(0, -0.35, -1).normalize().applyQuaternion(hand.quaternion);
        up = new THREE.Vector3(side === 'left' ? -1 : 1, 0.2, 0)
          .normalize()
          .applyQuaternion(hand.quaternion);
      }
      this.orientHand(arm.end, side, fwd, up);
    } else {
      // Relaxed hand: fingers down and a touch forward, palm toward the thigh, so the back
      // of the hand faces out and a little forward.
      this.orientHand(
        arm.end,
        side,
        new THREE.Vector3().addScaledVector(UP, -1).addScaledVector(bodyForward, 0.15),
        sideDir.clone().addScaledVector(bodyForward, 0.35),
      );
    }
  }

  private orientHand(rest: BoneRest, side: Side, forward: THREE.Vector3, up: THREE.Vector3): void {
    // Rest: fingers point along restDir, back of hand points up (+Y in T-pose).
    const f = forward.clone().normalize();
    const u = up.clone().normalize();
    const r = new THREE.Vector3().crossVectors(u, f).normalize();
    u.crossVectors(f, r).normalize();
    _m.makeBasis(r, u, f);
    const target = new THREE.Quaternion().setFromRotationMatrix(_m);
    const rf = rest.restDir.clone();
    const ru = new THREE.Vector3(0, 1, 0);
    const rr = new THREE.Vector3().crossVectors(ru, rf).normalize();
    ru.crossVectors(rf, rr).normalize();
    _m.makeBasis(rr, ru, rf);
    const restBasis = new THREE.Quaternion().setFromRotationMatrix(_m);
    _q2.copy(target).multiply(restBasis.invert()).multiply(rest.restWorldQuat);
    void side;
    setWorldQuaternion(rest.bone, _q2);
    rest.bone.updateWorldMatrix(false, true);
  }

  private applyFingers(side: Side, hand: SampledHand | null): void {
    // A controller is always held: the fingers rest round it even when no button is pressed,
    // instead of sticking out straight as if pointing.
    const held = [0.35, 0.3, 0.55, 0.6, 0.6];
    const curl = hand
      ? hand.kind === 'controller'
        ? hand.curl.map((c, i) => Math.max(c, held[i]!))
        : hand.curl
      : [0.2, 0.25, 0.3, 0.32, 0.35];
    const t = this.time + this.idlePhase + (side === 'left' ? 0 : 1.7);
    this.bones.fingers[side].forEach((finger, fi) => {
      // Relaxed fingers move a touch on their own; held or tracked fingers are left alone.
      const fidget = hand ? 0 : this.stillness * 0.06 * Math.sin(t * (0.5 + fi * 0.13) + fi);
      const c = THREE.MathUtils.clamp((curl[fi] ?? 0) + fidget, 0, 1);
      finger.phalanges.forEach((p, pi) => {
        const angle = c * (fi === 0 ? 0.5 : pi === 0 ? 1.2 : 1.4);
        _q1.setFromAxisAngle(p.axisLocal, angle);
        p.bone.quaternion.copy(p.restLocal).multiply(_q1);
      });
    });
  }

  private applyLegs(
    hips: THREE.Vector3,
    bodyForward: THREE.Vector3,
    scale: number,
    dt: number,
    moving: number,
  ): void {
    const sideDir = new THREE.Vector3().crossVectors(UP, bodyForward).normalize();
    const floorY = this.root.position.y;
    const idealFor = (side: Side) =>
      hips
        .clone()
        .addScaledVector(sideDir, (side === 'left' ? 1 : -1) * this.hipWidth * 0.6 * scale)
        .addScaledVector(bodyForward, 0.02)
        .setY(floorY);
    const bodyYaw = Math.atan2(-bodyForward.x, -bodyForward.z);
    if (!this.feetInitialised || moving === 2) {
      // First pose or a teleport: stand square at once.
      for (const side of ['left', 'right'] as Side[]) {
        const f = this.feet[side];
        f.planted.copy(idealFor(side));
        f.from.copy(f.planted);
        f.to.copy(f.planted);
        f.progress = 1;
        f.yaw = f.fromYaw = f.toYaw = bodyYaw;
      }
      this.feetInitialised = true;
    }
    // Gait: the faster the body moves, the longer and quicker the steps, and each foot lands
    // ahead of the hips by where they will be when it lands. Steps alternate; the next one may
    // begin as the current one passes half way, so a walk never falls behind. Standing still,
    // a foot steps when it drifts or when the body has turned away from it.
    const speed = this.velocity.length();
    const walking = speed > 0.25;
    const stepTime = walking ? THREE.MathUtils.clamp(0.36 - 0.07 * speed, 0.22, 0.36) : 0.3;
    const threshold = walking ? THREE.MathUtils.clamp(0.12 + 0.12 * speed, 0.14, 0.36) : 0.22;
    const busy = (['left', 'right'] as Side[]).filter((s) => this.feet[s].progress < 1);
    const canStart = busy.length === 0 || (busy.length === 1 && this.feet[busy[0]!].progress > 0.5);
    if (canStart) {
      const candidates = (['left', 'right'] as Side[]).filter((s) => this.feet[s].progress >= 1);
      // Prefer the foot that did not step last.
      candidates.sort((a, b) => (a === this.lastStep ? 1 : 0) - (b === this.lastStep ? 1 : 0));
      for (const side of candidates) {
        const f = this.feet[side];
        const drift = idealFor(side).distanceTo(f.planted);
        const turned = Math.abs(angleTo(f.yaw, bodyYaw)) > THREE.MathUtils.degToRad(22);
        if (drift > threshold || turned) {
          f.from.copy(f.planted);
          f.to
            .copy(idealFor(side))
            .addScaledVector(this.velocity, walking ? stepTime * 0.9 : 0)
            .setY(floorY);
          f.fromYaw = f.yaw;
          f.toYaw = bodyYaw;
          f.progress = 0;
          this.lastStep = side;
          break;
        }
      }
    }
    for (const side of ['left', 'right'] as Side[]) {
      const f = this.feet[side];
      const leg = this.bones.legs[side];
      if (f.progress < 1) {
        f.progress = Math.min(1, f.progress + dt / stepTime);
        const e = f.progress * f.progress * (3 - 2 * f.progress);
        f.planted.lerpVectors(f.from, f.to, e);
        f.planted.y = floorY + Math.sin(f.progress * Math.PI) * (walking ? 0.1 : 0.06);
        f.yaw = f.fromYaw + angleTo(f.fromYaw, f.toYaw) * e;
      }
      if (!leg) continue;
      const footForward = new THREE.Vector3(-Math.sin(f.yaw), 0, -Math.cos(f.yaw));
      const hip = leg.upper.bone.getWorldPosition(new THREE.Vector3());
      const ankleTarget = f.planted.clone().addScaledVector(UP, 0.07 * scale);
      // Knees point between the body and the foot, so a planted foot never crosses the leg.
      const pole = bodyForward
        .clone()
        .add(footForward)
        .normalize()
        .addScaledVector(UP, 0.2)
        .normalize();
      const knee = solveTwoBone(
        hip,
        ankleTarget,
        pole,
        leg.lengthUpper * scale,
        leg.lengthLower * scale,
      );
      aimBone(leg.upper, knee.clone().sub(hip), this.frame);
      leg.upper.bone.updateWorldMatrix(false, true);
      const kneeNow = leg.lower.bone.getWorldPosition(new THREE.Vector3());
      aimBone(leg.lower, ankleTarget.clone().sub(kneeNow), this.frame);
      leg.lower.bone.updateWorldMatrix(false, true);
      // Foot flat, pointing where it was planted.
      aimBone(
        leg.end,
        footForward.addScaledVector(UP, -0.15),
        _q2.setFromAxisAngle(UP, angleTo(this.yaw, f.yaw)).multiply(this.frame),
      );
      leg.end.bone.updateWorldMatrix(false, true);
    }
  }
}

function vrmName(key: string): string {
  const map: Record<string, string> = {
    hips: 'hips',
    spine: 'spine',
    spine1: 'chest',
    spine2: 'upperChest',
    neck: 'neck',
    head: 'head',
    headTop: 'head',
    leftArm: 'leftUpperArm',
    leftForeArm: 'leftLowerArm',
    leftHand: 'leftHand',
    rightArm: 'rightUpperArm',
    rightForeArm: 'rightLowerArm',
    rightHand: 'rightHand',
    leftUpLeg: 'leftUpperLeg',
    leftLeg: 'leftLowerLeg',
    leftFoot: 'leftFoot',
    leftToe: 'leftToes',
    rightUpLeg: 'rightUpperLeg',
    rightLeg: 'rightLowerLeg',
    rightFoot: 'rightFoot',
    rightToe: 'rightToes',
  };
  return map[key] ?? key;
}
